import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { APICreateMentorValueProposition, APIUpdateMentorValuePropositionByID, APIDeleteMentorValueProposition } from "utils/api";
import { Formik, Field } from "formik";
import * as Yup from "yup";

interface ValueProposition {
  id: string;
  title: string;
  mentor_id: string;
}

interface ValuePropositionCardProps {
  mentorId: string;
  valueProposition?: ValueProposition;
  onCancel?: () => void;
}

const validationSchema = Yup.object().shape({
  title: Yup.string().required("Title is required"),
});

const ValuePropositionCard: React.FC<ValuePropositionCardProps> = ({
  mentorId,
  valueProposition,
  onCancel,
}) => {
  const [isEditing, setIsEditing] = useState(!valueProposition);
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: (data: { title: string, mentor_id: string }) =>
      APICreateMentorValueProposition(mentorId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mentorValueProposition", mentorId] });
      onCancel?.();
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: { id: string; title: string }) =>
      APIUpdateMentorValuePropositionByID(mentorId, data.id, { title: data.title }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mentorValueProposition", mentorId] });
      setIsEditing(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => APIDeleteMentorValueProposition(mentorId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mentorValueProposition", mentorId] });
    },
  });

  const initialValues = {
    title: valueProposition?.title || "",
  };

  const handleSubmit = (values: { title: string }) => {
    if (valueProposition) {
      updateMutation.mutate({ id: valueProposition.id, title: values.title });
    } else {
      createMutation.mutate({ title: values.title, mentor_id: `${mentorId}` });
    }
  };

  const handleDelete = () => {
    if (valueProposition) {
      deleteMutation.mutate(valueProposition.id);
    }
  };

  return (
    <div className="flex items-center gap-[15px] flex-row">
      <svg width="28" height="24" viewBox="0 0 28 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path id="Vector" fillRule="evenodd" clipRule="evenodd" d="M21.9889 1.06284C21.4096 1.58317 15.6377 6.74093 11.5346 10.7735C11.1474 11.1507 10.7752 11.5215 10.424 11.8759C8.52708 10.2662 6.43803 8.7507 5.93978 8.39297C4.56208 7.0206 2.39199 7.05963 1.04131 8.52305C-0.306372 9.98323 -0.345392 12.3247 0.921246 13.8369C1.28143 14.4223 2.86623 16.9882 4.47204 19.1508C5.82272 20.9687 7.1734 22.4288 7.23643 22.4971C9.08536 24.4971 12.0719 24.5004 13.9238 22.5101C14.0078 22.4223 15.8598 20.4646 18.0569 17.8402C21.7818 13.3849 26.5542 7.12142 27.0194 6.50678C28.3461 5.01083 28.3281 2.61082 26.9564 1.12788C25.5907 -0.355058 23.3816 -0.374569 21.9889 1.06284Z" fill="url(#paint0_linear_1145_4753)" />
        <defs>
          <linearGradient id="paint0_linear_1145_4753" x1="0" y1="0" x2="0" y2="24" gradientUnits="userSpaceOnUse">
            <stop stopColor="#1B4E88" />
            <stop offset="1" stopColor="#5FA6FF" />
          </linearGradient>
        </defs>
      </svg>

        {isEditing ? (
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={(values, { setSubmitting }) => {
              handleSubmit(values);
              setSubmitting(false);
            }}
          >
            {({ isSubmitting, values }) => (
              <div className="flex flex-row gap-4 w-full">
                <Field
                  name="title"
                  type="text"
                  placeholder="Enter value proposition title"
                  className="w-full rounded-[15px] p-[31px] focus:ring-2 focus:ring-[#407EC9] shadow-[0px_7px_29px_0px_rgba(100,100,111,0.20)] border-none outline-none bg-white"
                />
                <div className="flex gap-4">
                  {valueProposition && (
                    <button
                      type="button"
                      onClick={handleDelete}
                      className="text-red-500 hover:text-red-700"
                    >
                      Delete
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      if (valueProposition) {
                        setIsEditing(false);
                      } else {
                        onCancel?.();
                      }
                    }}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSubmit(values)}
                    disabled={isSubmitting}
                    className="rounded-lg !h-[50px] my-auto bg-[#407EC9] px-6 py-3 text-white transition-colors hover:bg-[#3569A7] disabled:opacity-50"
                  >
                    {isSubmitting ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            )}
          </Formik>
        ) : (
      <div className="rounded-[20px] h-full w-full bg-white p-[31px] shadow-[0px_7px_29px_0px_rgba(100,100,111,0.2)]">
          <div className="flex justify-between items-center">
            <p className="custom-text-[poppins-400-19px] text-[#6B7280]">
              {valueProposition?.title}
            </p>
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="text-[#407EC9] hover:text-[#3569A7]"
            >
              Edit
            </button>
          </div>
      </div>
        )}
      </div>
  );
};

export default ValuePropositionCard; 